import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Lock, Car, CalendarClock, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import RouteMap from "@/components/RouteMap";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export default function BookingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [quote, setQuote] = useState(null);
  const [form, setForm] = useState({ customer_name: "", customer_email: "", customer_phone: "", flight_number: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState(null);

  useEffect(() => {
    const load = async () => {
      let q = location.state?.quote;
      if (!q) { const s = sessionStorage.getItem("pendingQuote"); if (s) q = JSON.parse(s); }
      const resume = searchParams.get("resume");
      if (!q && resume) {
        try { const { data } = await api.get(`/quote/${resume}`); q = data; } catch { /* ignore */ }
      }
      if (!q) { navigate("/"); return; }
      sessionStorage.setItem("pendingQuote", JSON.stringify(q));
      setQuote(q);
    };
    load();
  }, [location.state, navigate, searchParams]);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, customer_name: f.customer_name || user.name, customer_email: f.customer_email || user.email }));
  }, [user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pay = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data: booking } = await api.post("/bookings", { quote_id: quote.id, ...form });
      const { data } = await api.post("/payments/checkout", { booking_id: booking.booking_id, origin_url: window.location.origin });
      if (data?.url) {
        setCheckoutUrl(data.url);
        setTimeout(() => { window.location.assign(data.url); }, 80);
      } else {
        toast.error("Could not start checkout. Please try again.");
        setLoading(false);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Payment could not be started. Please try again.");
      setLoading(false);
    }
  };

  if (!quote) return <div className="min-h-screen bg-background" />;
  const fld = "bg-secondary border-white/10 rounded-none h-12";

  if (checkoutUrl) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6" data-testid="checkout-redirect">
        <div className="max-w-md w-full border border-white/10 bg-[#121212] p-10 text-center">
          <Lock className="text-primary mx-auto mb-6" size={44} />
          <h1 className="text-2xl text-foreground mb-3">Redirecting to secure payment…</h1>
          <p className="text-muted-foreground mb-8">
            You&apos;re being sent to our secure Stripe checkout. If the page doesn&apos;t open
            automatically (some browsers or ad-blockers may delay it), tap the button below.
          </p>
          <a href={checkoutUrl} data-testid="checkout-continue-link"
            className="inline-flex items-center justify-center gap-2 w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 uppercase tracking-wide">
            <Lock size={16} /> Continue to Secure Payment
          </a>
          <button onClick={() => setCheckoutUrl(null)} data-testid="checkout-back"
            className="mt-4 text-sm text-muted-foreground hover:text-primary">Back to booking</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-10 px-6">
      <div className="max-w-5xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary mb-8 text-sm" data-testid="back-home">
          <ArrowLeft size={16} /> Back
        </Link>
        <h1 className="text-3xl sm:text-4xl text-foreground mb-2">Complete Your Reservation</h1>
        <p className="text-muted-foreground mb-10">You&apos;re moments away from a first-class ride.</p>

        <div className="grid lg:grid-cols-5 gap-8">
          <motion.form initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} onSubmit={pay}
            data-testid="booking-form" className="lg:col-span-3 border border-white/10 bg-[#121212] p-8 space-y-5">
            <h3 className="text-xl text-foreground mb-2">Passenger Details</h3>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Full Name</Label>
              <Input required data-testid="cust-name" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} className={fld} />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Email</Label>
                <Input required type="email" data-testid="cust-email" value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} className={fld} />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Phone</Label>
                <Input required data-testid="cust-phone" value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} className={fld} />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Flight Number (optional)</Label>
              <Input data-testid="cust-flight" value={form.flight_number} onChange={(e) => set("flight_number", e.target.value)} className={fld} placeholder="e.g. UA1234" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Special Requests (optional)</Label>
              <Textarea data-testid="cust-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="bg-secondary border-white/10 rounded-none" rows={3} placeholder="Child seat, extra stops, etc." />
            </div>
            <Button type="submit" disabled={loading} data-testid="pay-btn"
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase tracking-wide h-13 py-4">
              <Lock size={16} className="mr-2" /> {loading ? "Redirecting to secure checkout..." : `Pay $${quote.total.toFixed(2)} & Reserve`}
            </Button>
            <p className="text-xs text-muted-foreground text-center">Secured by Stripe. You&apos;ll be redirected to complete payment.</p>
            {!user && <p className="text-xs text-center text-muted-foreground">Tip: <Link to="/register" className="text-primary">create an account</Link> to manage this booking later.</p>}
          </motion.form>

          <div className="lg:col-span-2">
            <div className="border border-white/10 bg-[#121212] p-6 sticky top-6" data-testid="booking-summary">
              {quote.pickup_lat && quote.dropoff_lat && (
                <div className="mb-5 -mx-6 -mt-6">
                  <RouteMap origin={{ lat: quote.pickup_lat, lng: quote.pickup_lng }} destination={{ lat: quote.dropoff_lat, lng: quote.dropoff_lng }} />
                </div>
              )}
              <h3 className="text-lg text-foreground mb-5">Trip Summary</h3>
              <div className="space-y-4 text-sm">
                <div className="flex gap-3"><MapPin size={16} className="text-primary shrink-0 mt-0.5" /><div><div className="text-muted-foreground text-xs">From</div><div className="text-foreground">{quote.pickup}</div></div></div>
                {quote.service_type === "hourly" ? (
                  <div className="flex gap-3"><Clock size={16} className="text-primary shrink-0 mt-0.5" /><div><div className="text-muted-foreground text-xs">Duration</div><div className="text-foreground">{quote.hours} hours · {quote.free_miles} free miles</div></div></div>
                ) : (
                  <div className="flex gap-3"><MapPin size={16} className="text-primary shrink-0 mt-0.5" /><div><div className="text-muted-foreground text-xs">To</div><div className="text-foreground">{quote.dropoff}{quote.num_stops ? ` (${quote.num_stops} stop${quote.num_stops > 1 ? "s" : ""})` : ""}</div></div></div>
                )}
                <div className="flex gap-3"><CalendarClock size={16} className="text-primary shrink-0 mt-0.5" /><div><div className="text-muted-foreground text-xs">When</div><div className="text-foreground">{quote.date} · {quote.time}</div></div></div>
                <div className="flex gap-3"><Car size={16} className="text-primary shrink-0 mt-0.5" /><div><div className="text-muted-foreground text-xs">Vehicle</div><div className="text-foreground">{quote.vehicle_name} · {quote.passengers} pax</div></div></div>
              </div>
              <div className="border-t border-white/10 mt-5 pt-5 space-y-2 text-sm">
                <div className="flex justify-between text-muted-foreground"><span>{quote.service_type === "hourly" ? `${quote.hours}h × $${quote.hourly_rate}/hr` : "Base fare"}</span><span className="text-foreground">${quote.base_fare.toFixed(2)}</span></div>
                {quote.meet_greet_fee > 0 && <div className="flex justify-between text-muted-foreground"><span>Meet & Greet</span><span className="text-foreground">${quote.meet_greet_fee.toFixed(2)}</span></div>}
                <div className="flex justify-between text-muted-foreground"><span>Tip{quote.tip_percent ? ` (${Math.round(quote.tip_percent)}%)` : ""}</span><span className="text-foreground">${(quote.tip ?? 0).toFixed(2)}</span></div>
                {quote.discount > 0 && <div className="flex justify-between text-primary"><span>Promo {quote.promo_applied}</span><span>−${quote.discount.toFixed(2)}</span></div>}
                <div className="flex justify-between items-end pt-3 border-t border-white/10 mt-2">
                  <span className="uppercase tracking-wide text-muted-foreground">Total</span>
                  <span className="text-2xl font-heading text-primary">${quote.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
